'use client'

import { useRef, useState } from 'react'
import Image from 'next/image'
import Link from 'next/link'
import { Input } from '@/components/ui/input'
import { trackLandingEvent } from '@/lib/landing-analytics'

interface LandingHeroProps {
  buildLoginRedirect: (target: string) => string
}

const RECOMMENDED_KEYWORDS = [
  'Next.js',
  'React',
  'DevOps',
  'AWS',
  'TypeScript',
]

const HERO_SUPPORT_TAGS = [
  '베타 무료',
  '개인·팀 가능',
  '응답 빠른 멤버 우선 추천',
]

/** ✅ 내용만 (모바일/데스크탑 공통) */
function HeroActionContent({
  buildLoginRedirect,
}: {
  buildLoginRedirect: (target: string) => string
}) {
  const [keywordInput, setKeywordInput] = useState('')
  const lastTrackedKeyword = useRef('')
  const selectedKeywords = Array.from(
    new Set(
      keywordInput
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean)
    )
  )
  const selectedKeywordCount = selectedKeywords.length

  const trackKeywordSaved = (source: 'chip' | 'input_blur') => {
    const normalized = selectedKeywords.join(', ')
    if (!normalized || normalized === lastTrackedKeyword.current) return

    lastTrackedKeyword.current = normalized
    trackLandingEvent('keyword_saved', {
      source,
      keyword_count: selectedKeywordCount,
      keywords: normalized,
    })
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
    }
  }

  const handleKeywordClick = (keyword: string) => {
    setKeywordInput((prev) => {
      const current = prev
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean)
      if (current.includes(keyword)) return prev

      const nextKeywords = [...current, keyword]
      const normalized = nextKeywords.join(', ')
      if (normalized !== lastTrackedKeyword.current) {
        lastTrackedKeyword.current = normalized
        trackLandingEvent('keyword_saved', {
          source: 'chip',
          keyword_count: nextKeywords.length,
          keywords: normalized,
        })
      }

      return normalized
    })
  }

  const onboardingTarget = keywordInput.trim()
    ? `/onboarding/freelancer/role?keywords=${encodeURIComponent(keywordInput.trim())}`
    : '/onboarding/freelancer/role'

  return (
    <div className="relative w-full rounded-2xl border border-white/25 bg-[#111a2d]/80 p-4 shadow-[0_18px_45px_rgba(0,0,0,0.45)] backdrop-blur-md md:p-5">
      <h3 className="text-2xl font-bold leading-tight text-white">프리랜서 프로필 등록</h3>
      <p className="mt-1 text-sm text-white/80">3분이면 완료, 등록 후 프로젝트 요청 알림을 받습니다.</p>

      <Link
        href={buildLoginRedirect(onboardingTarget)}
        data-landing-event="click_cta_hero_primary"
        className="mt-4 inline-flex h-12 w-full items-center justify-center whitespace-nowrap rounded-xl bg-blue-600 px-4 text-center text-base font-semibold leading-none text-white transition-colors hover:bg-blue-700 sm:px-6 sm:text-lg"
      >
        지금 프로필 등록하기
      </Link>

      <p className="mt-3 text-xs font-medium text-white/85">추천용 키워드 입력(선택)</p>
      <Input
        value={keywordInput}
        onChange={(e) => setKeywordInput(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={() => trackKeywordSaved('input_blur')}
        className="mt-2 h-11 rounded-lg border-white/30 bg-white/95 text-sm text-slate-900 placeholder:text-slate-400 focus-visible:border-blue-500 focus-visible:ring-blue-500/20"
        placeholder="예: Next.js, React, DevOps, AWS"
      />

      <div className="mt-2 hidden flex-wrap gap-2 md:flex">
        {RECOMMENDED_KEYWORDS.map((keyword) => (
          <button
            key={keyword}
            onClick={() => handleKeywordClick(keyword)}
            className="rounded-full border border-white/30 bg-white/95 px-3 py-1 text-xs font-medium text-slate-700 transition-colors hover:bg-white"
          >
            {keyword}
          </button>
        ))}
      </div>
      <p className="mt-2 text-xs text-white/70">
        {selectedKeywordCount > 0
          ? `추천에 ${selectedKeywordCount}개 키워드가 즉시 반영되었습니다.`
          : '입력값은 온보딩에서 자동 반영됩니다.'}
      </p>
    </div>
  )
}

export default function LandingHero({ buildLoginRedirect }: LandingHeroProps) {
  return (
    <section className="overflow-hidden rounded-none md:mx-6 md:mt-6 md:rounded-3xl lg:mx-8">
      <div className="relative min-h-[520px] md:min-h-[620px]">
        <div className="absolute inset-0">
          <Image src="/images/landing/hero.jpg" alt="프리랜서 랜딩 배경 이미지" fill priority className="object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/55 to-black/45" />
          <div className="absolute inset-x-0 bottom-0 h-28 bg-gradient-to-b from-transparent to-black/35" />
        </div>

        <div className="relative z-10 mx-auto grid h-full w-full max-w-6xl grid-cols-1 gap-6 px-4 pb-8 pt-10 md:grid-cols-[1.2fr_0.9fr] md:items-start md:gap-10 md:pt-20">
          <div className="text-left text-white">
            <h1
              className="mb-3 text-[34px] font-semibold leading-[1.16] tracking-tight sm:text-[40px] md:mb-4 md:text-[62px]"
              style={{ wordBreak: 'keep-all', textShadow: '0 2px 10px rgba(0,0,0,0.45)' }}
            >
              <span className="block">프로필만 등록하면,</span>
              <span className="block">프로젝트 제안 기회가 열립니다</span>
            </h1>
            <p className="max-w-2xl text-sm font-normal leading-relaxed text-white/90 md:text-xl" style={{ textShadow: '0 1px 6px rgba(0,0,0,0.35)' }}>
              키워드 기반으로 맞는 요청을 추천해드립니다
            </p>

            <div className="mt-4 flex flex-wrap gap-2">
              {HERO_SUPPORT_TAGS.map((tag) => (
                <span
                  key={tag}
                  className="rounded-full border border-white/25 bg-white/95 px-3 py-1 text-xs font-medium text-slate-700 md:text-sm"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>

          <div className="w-full md:pt-2">
            <HeroActionContent buildLoginRedirect={buildLoginRedirect} />
          </div>
        </div>
      </div>
    </section>
  )
}
